import type { MarketplaceConfig } from "@slfinrtl/feature-config"
import {
	AccidentOptions,
	FullServiceHistoryOptions,
	MaintenanceScopeOptions,
	ReconditioningOptions,
	VehicleOrigin,
} from "@slfinrtl/ui-bff-routes"
import type { TransformationRules } from "src/common/transformValues/transformValues"
import type { InventoryInformationFormValues } from "../inventoryAndIntakeForm"

/**
 * If an effect sets a value to null, it is only transformed to null if the value was set previously. Otherwise, it will be set to undefined
 */
export const getInventoryInformationChangeRules = (
	config: MarketplaceConfig,
): TransformationRules<Partial<InventoryInformationFormValues>> => {
	return [
		{
			effect: {
				availableFromDate: undefined,
				expectedArrivalDate: undefined,
			},
			trigger: { stockStatus: "IN_STOCK" },
		},
		{
			effect: { arrivalDate: undefined, availableFromDate: undefined },
			trigger: { stockStatus: "INCOMING" },
		},
		{
			effect: { expectedArrivalDate: undefined },
			trigger: { stockStatus: "IN_USE" },
		},
		{
			effect: { stockStatus: "IN_STOCK" },
			trigger: {
				$oneOf: {
					key: "vehicleType",
					values: ["NEW_CAR", "USED_CAR", "COMMISSION_CAR"],
				},
				stockStatus: "IN_USE",
			},
		},
		{
			effect: {
				reconditioningAmount: undefined,
				reconditioningApproximateAmount: undefined,
			},
			trigger: { reconditioned: ReconditioningOptions.NO_SELECTION },
		},
		{
			effect: { reconditioningApproximateAmount: undefined },
			trigger: { reconditioned: ReconditioningOptions.RECONDITIONED },
		},
		{
			effect: { reconditioningAmount: undefined },
			trigger: { reconditioned: ReconditioningOptions.NOT_RECONDITIONED },
		},
		{
			effect: { previousOwnerUsageHistory: null },
			trigger: { previousOwners: "0" },
		},
		{
			effect: { fullServiceHistoryInPorscheCenter: false },
			trigger: { fullServiceHistory: FullServiceHistoryOptions.INCOMPLETE },
		},
		{
			effect: {
				accidentDescription: null,
				accidentFree: AccidentOptions.ACCIDENT_FREE,
				batteryCapacityPercentage: null,
				firstRegistrationDate: null,
				formerRentalCar: false,
				fullServiceHistory: FullServiceHistoryOptions.COMPLETE,
				fullServiceHistoryInPorscheCenter: true,
				latestMaintenanceDate: null,
				latestMaintenanceMileage: null,
				latestMaintenanceScope: MaintenanceScopeOptions.NO_SELECTION,
				latestMaintenanceWorkshop: null,
				nextGeneralInspectionDate: null,
				nonSmoker: true,
				previousDamage: false,
				previousDamageAmount: null,
				previousDamageDescription: null,
				previousOwners: "0",
				previousOwnerUsageHistory: null,
				renewGeneralInspectionOnSale: false,
			},
			trigger: ({ vehicleType }) => vehicleType === "NEW_CAR",
		},
		{
			effect: {
				vatDeductible: config.vatDeductibleAvailable,
			},
			trigger: (values) =>
				values.vehicleType === "NEW_CAR" && values.pricingUseCase === "ALWAYS_VAT_DEDUCTIBLE_BASIC",
		},
		{
			effect: { accidentDescription: null },
			trigger: { accidentFree: AccidentOptions.ACCIDENT_FREE },
		},
		{
			effect: { vehicleOrigin: VehicleOrigin.COMMISSION_CAR },
			trigger: { vehicleType: "COMMISSION_CAR" },
		},
		{
			effect: { vehicleOrigin: undefined },
			trigger: {
				$not: { vehicleType: "COMMISSION_CAR" },
				vehicleOrigin: VehicleOrigin.COMMISSION_CAR,
			},
		},
	]
}
